import { produce, type WritableDraft } from 'immer';
import { isUndefined, set } from 'lodash';
import {
  RestLossAversionCardTemplates,
  type RestLossAversionCard,
} from '@bbkAdminUtils/api-client/rest-la-cards-types';
import {
  type HeroImageV0,
  type RestModalCard,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type RestLossAversion } from '@bbkAdminUtils/api-client/rest-offers-api';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

const reportDeprecatedModalUsage = (type: string, card: RestModalCard) => {
  GlobalLogger.warn(`Deprecated offer modal: ${type} ${card.template}`, card);
};

export const isHeroImageV0 = (hero: unknown): hero is HeroImageV0 =>
  (hero as HeroImageV0)?.img !== undefined;

const migrateHeroV0ToHeroV1 = (draftCard: WritableDraft<RestModalCard>) => {
  if (
    draftCard.requires &&
    'hero' in draftCard.requires &&
    isHeroImageV0(draftCard.requires.hero)
  ) {
    reportDeprecatedModalUsage('migrateHeroV0ToHeroV1', draftCard);
    draftCard.requires.hero = {
      show: 'image',
      url: draftCard.requires.hero.img,
    };
  }
};

const migrateHeaderToInfo = (draftCard: WritableDraft<RestModalCard>) => {
  if (
    draftCard.requires &&
    'header' in draftCard.requires &&
    !isUndefined(draftCard.requires.header)
  ) {
    reportDeprecatedModalUsage('migrateHeaderToInfo', draftCard);
    (draftCard.requires as any).info = draftCard.requires.header;
    draftCard.requires.header = undefined;
  }
};

const migrateSuggestionToInfo = (draftCard: WritableDraft<RestModalCard>) => {
  if (
    draftCard.requires &&
    'suggestion' in draftCard.requires &&
    !isUndefined(draftCard.requires.suggestion)
  ) {
    reportDeprecatedModalUsage('migrateSuggestionToInfo', draftCard);
    (draftCard.requires as any).info = draftCard.requires.suggestion;
    draftCard.requires.suggestion = undefined;
  }
};

const migrations = [
  migrateHeroV0ToHeroV1,
  migrateHeaderToInfo,
  migrateSuggestionToInfo,
];
export const migrateCard = (card: RestModalCard) =>
  migrations.reduce(
    (updatedCard, migration) =>
      produce(updatedCard, (draftCard) => migration(draftCard)),
    card
  );

const imageCtaSetToImage = (
  draftCard: WritableDraft<RestLossAversionCard | RestModalCard>
) => {
  if (draftCard.template === RestLossAversionCardTemplates.la_one_up_image) {
    set(draftCard, 'requires.forwards.text', 'Image');
  }
};

export const migrateLossAversion = (la: RestLossAversion) =>
  produce(la, (draftLa) => {
    const draftPrimaryCard = draftLa.cards.find(
      (c) => c.name === draftLa.primary_card_name
    );
    if (draftPrimaryCard) {
      imageCtaSetToImage(draftPrimaryCard);
    }
  });
